<!--
Public: Renders an intentionally simple modal and provides a slot for components.

<modal ref='modal' modal-container-classes = "w-800">
  <div>
    <div>You can put in basic HTML</div>
    <input-entity-autocomplete></input-entity-autocomplete>
    <div>Or even another component</div>
  </div>
</modal>
-->
<template>
  <transition name="modal">
    <div :class       = "maskClasses"
         v-if         = "showModal"
         @keydown.esc = "showModal = false">
      <div ref="modalWrapper" :class = "wrapperClasses">
        <focus-trap :active="false" ref="modalFocusTrap">
          <div :class   = "containerClasses"
               :style   = "internalMaxWidth"
               :id      = "modalContainerId"
               @click   = "onPreventClick"
               tabindex = "-1">
            <i class="bi bi-x-lg modal-x-icon" @click="hide"></i>
            <slot></slot>
          </div><!-- containerClasses -->




        </focus-trap>
      </div><!-- wrapperClasses -->
    </div>
  </transition>
</template>

<script>
  import { isMobile,
           toBoolean }  from '../lib/utils.js'
  import { FocusTrap }  from 'focus-trap-vue'

  export default {
    components: {
      FocusTrap
    },

    props: {
      // Max width for the modal-container.
      maxWidth:              { default: '' },
      // classes for the modal mask.
      modalMaskClasses:      { default: '' },
      // classes for the modal wrapper.
      modalWrapperClasses:   { default: '' },
      // classes for the modal container.
      modalContainerClasses: { default: '' },
      // id for the modal container.
      modalContainerId:      { default: '' },
      // Prevent click event from bubbling if true.
      preventClick:          { default: true }
    },

    data() {
      return {
        // Public: Whether to display the modal.
        showModal: false
      }
    },

    computed: {
      // Internal: Classes for outermost div.
      maskClasses() {
        const fixed = isMobile() ? '' : 'fixed'

        return `modal-mask ${fixed} ${this.modalMaskClasses}`
      },

      // Internal: Wrapper classes for modal.
      wrapperClasses() {
        return `modal-wrapper ${this.modalWrapperClasses}`
      },

      // Internal: Classes for innermost div. Applied to modal.
      containerClasses() {
        return `ui-scrollbar modal-container position-relative ${this.modalContainerClasses}`
      },

      // Internal: Set a max-width for the modal-container.
      internalMaxWidth() {
        return `max-width: ${this.maxWidth}px;`
      },

      // Internal: Import function from utils to determin if mobile device.
      isMobile: isMobile
    },

    methods: {
      // Public: Hide the modal.
      hide() {
        this.showModal = false
        this.$nextTick(() => {
          this.$refs.modalFocusTrap.deactivate()
        })
      },
      // Public: Show the modal.
      show() {
        this.showModal = true
        this.$nextTick(() => {
          this.$refs.modalFocusTrap.activate()
        })
      },

      // Internal: Selectively prevent the default click event.
      onPreventClick(event) {
        if (toBoolean(this.preventClick)) {
          event.preventDefault()
        }
      },

      // Internal: handles keydown event for escape key
      closeOnEscape(event) {
        if (event.key === 'Escape') {
          this.hide()
        }
      }
    },

    mounted() {
      document.addEventListener('keydown', this.closeOnEscape)
    }
  }
</script>

<style scoped lang="scss">
  @use "../../assets/stylesheets/colors.scss" as *;
  @use "../../assets/stylesheets/breakpoints.scss" as *;

  .modal-x-icon {
    font-size: 1.4em;
    position: absolute;
    top: 10px;
    right: 20px;
    color: rgba(125, 125, 125, .8);

    &:hover {
      color: rgba(125, 125, 125, 1);
    }
  }

  .modal-mask {
    z-index: 9998;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.2);
  }

  .modal-wrapper {
    z-index: 9997;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: scroll;
    background: rgba(50,50,50, 0.5);
  }

  .modal-container {
    z-index: 9999;
    overflow-y: auto;
    margin: 1% auto;
    padding-top: 50px; // Ensures space for X
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 20px;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
    transition: all 0.3s ease;
    border-radius: 10px;
    width: 80vw;

    @media (max-width: $breakpoint-md) {
      width: 90vw;
    }
  }

  .modal-enter-active, .modal-leave-active {
    transition: opacity .5s;
  }

  .modal-enter, .modal-leave-to {
    opacity: 0;
  }

  .scrollable-popup {
    max-height: calc(93% - 70px);
    cursor: auto;
  }

  .analytics-popup-max-height {
    max-height: calc(93% - 70px);
  }

  .create-public-link-max-height {
    max-height: calc(93% - 70px);
  }
</style>
